import {
	DeviceState,
	DeviceStatus,
} from "Infrastructure/Models/Messages/Dto/DeviceStatus";
import { FeatureDto } from "Infrastructure/Models/Messages/Dto/FeatureDto";
import { FunctionComponent, useEffect, useState } from "react";
import { CircularProgress, Stack } from "@mui/material";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import {
	FeatureEnableDisableMessage,
	FeatureUpdateMessage,
} from "Infrastructure/Models/Messages/Messages";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import InstanceResponseContent from "../InstanceResponseContent";
import FeatureActions from "./FeatureActions";
import { FeatureDeviceModel } from "Infrastructure/Utils/Nsure/FeatureUtils";

interface Props {
	deviceStatuses: DeviceStatus[] | null;
	deviceStatus?: DeviceStatus | undefined;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
}

const FeaturesDialogContent: FunctionComponent<Props> = (props) => {
	const { deviceStatuses, deviceStatus, hubConnection, closeDialog } = props;
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [featureDeviceList, setFeatureDeviceList] = useState<
		FeatureDeviceModel[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);

	const getFilteredStatuses = () =>
		deviceStatuses?.filter(
			(x) =>
				!x.isSelfInsurance &&
				x.deviceState === DeviceState.Connected &&
				(!!deviceStatus ? x === deviceStatus : true)
		) ?? [];

	const mergeFeaturesWithDevices = (
		featuresByDevice: Record<string, FeatureDto[]>
	) => {
		const featureMap = new Map<string, FeatureDeviceModel>();

		for (const status of getFilteredStatuses()) {
			const features = featuresByDevice[status.deviceName] ?? status.features ?? [];
			for (const feature of features) {
				if (!featureMap.has(feature.code!)) {
					featureMap.set(feature.code!, {
						feature,
						devices: [
							{ deviceName: status.deviceName, isEnabled: feature.isEnabled },
						],
					});
				} else {
					featureMap.get(feature.code!)!.devices.push({
						deviceName: status.deviceName,
						isEnabled: feature.isEnabled,
					});
				}
			}
		}

		setFeatureDeviceList(Array.from(featureMap.values()));
		setIsLoading(false);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: mergeFeaturesWithDevices is defined inside component
	useEffect(() => {
		if (!deviceStatuses) {
			return;
		}
		setIsLoading(true);
		const deviceNames = getFilteredStatuses().map((x) => x.deviceName);
		if (
			deviceNames.length === 0 ||
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			mergeFeaturesWithDevices({});
			return;
		}
		hubConnection
			.invoke<Record<string, FeatureDto[]>>("getDeviceFeaturesRequest", deviceNames)
			.then((result) => mergeFeaturesWithDevices(result ?? {}))
			.catch((error: Error) => {
				logError(error);
				mergeFeaturesWithDevices({});
			});
	}, [deviceStatuses]);

	const [messageResponse, setMessageResponse] = useState<{
		actionName: string;
		requestId?: string | null;
		deviceNames: string[];
	} | null>(null);

	const handleFeatureUpdate = (featureUpdateMessage: FeatureUpdateMessage) => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		setMessageResponse({
			actionName: "Aktualizace",
			requestId: featureUpdateMessage.requestId,
			deviceNames: featureUpdateMessage.filteredDeviceNames,
		});

		hubConnection
			.invoke("featureUpdate", featureUpdateMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	const handleFeatureEnableDisable = (
		featureEnableDisableMessage: FeatureEnableDisableMessage
	) => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		setMessageResponse({
			actionName: "Nastavení features",
			requestId: featureEnableDisableMessage.requestId,
			deviceNames: featureEnableDisableMessage.filteredDeviceNames,
		});

		hubConnection
			.invoke("featureEnableDisable", featureEnableDisableMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});
	};

	if (messageResponse) {
		return (
			<InstanceResponseContent
				actionName={messageResponse.actionName}
				requestId={messageResponse.requestId}
				deviceNames={messageResponse.deviceNames}
				hubConnection={hubConnection}
				closeDialog={closeDialog}
			/>
		);
	}

	return (
		<Stack direction="column">
			{isLoading ? (
				<>
					<CircularProgress />
				</>
			) : (
				<>
					{deviceStatuses && deviceStatuses?.length > 0 && (
						<FeatureActions
							deviceStatuses={deviceStatuses}
							deviceStatus={deviceStatus}
							featureDeviceList={featureDeviceList}
							hubConnection={hubConnection}
							closeDialog={closeDialog}
							onUpdate={handleFeatureUpdate}
							onEnableDisable={handleFeatureEnableDisable}
						/>
					)}
				</>
			)}
		</Stack>
	);
};

export default FeaturesDialogContent;
